import { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { Layer } from '~/packages/general/variable/spaceTime.types'
import {
  VariableAndOptionalResult,
  VariableWithResult,
} from '~/packages/general/variable/variables.typing'
import useMedia from 'react-use/lib/useMedia'
import { ReportWithLayer } from './modals/reports/reports.types'
import useAPI from '~/prix/react/hooks/api'
import recommendedLayerForVariable from '~/packages/general/variable/recommendedLayerForVariable.util'
import { useClickAway } from 'react-use'
import { faMagnifyingGlass, faXmark } from '@fortawesome/free-solid-svg-icons'
import { SpaceForPoint } from '~/prix/types/presets/space'
import { ButtonVariants } from '~/components/buttonVariants'
import {
  childrenItemViewLabels,
  spaceTypePluralLabels,
  spaceTypeSingularLabels,
} from '~/packages/general/variable/spaceTime.data'
import ModalSelectVariable from '~/components/mapContent/modals/variable/modalSelectVariable'
import ModalSelectSpace from '~/components/mapContent/modals/space/modalSelectSpace'
import ModalSelectTime from '~/components/mapContent/modals/modalSelectTime'
import { ToolsAndOptionalResult } from '~/packages/general/variable/tools.typing'

interface WrapperProps {
  $isOpen: boolean
}

const Wrapper = styled.aside<WrapperProps>`
  position: absolute;
  top: ${({ theme }) => theme.padding(1)};
  left: ${({ theme }) => theme.padding(1)};

  display: flex;
  flex-direction: column;

  min-height: 80px;
  max-height: 100px;
  max-width: 600px;
  width: 50%;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}px) {
    width: 80%;
  }

  .layer {
    display: flex;
    flex: 1;
    border-radius: ${({ theme }) => theme.borderRadius};
    box-shadow: ${({ theme }) => theme.shadow(1)};
    margin-bottom: ${({ theme }) => theme.padding(1)};
    margin-bottom: calc(${({ theme }) => theme.padding(1)} + 1px);

    background-color: white;
    @supports ((-webkit-backdrop-filter: none) or (backdrop-filter: none)) {
      background-color: ${({ theme }) => theme.backdropBackground};
      -webkit-backdrop-filter: ${({ theme }) => theme.backdropFilter};
      backdrop-filter: ${({ theme }) => theme.backdropFilter};
    }

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}px) {
      visibility: ${({ $isOpen }) => ($isOpen ? 'visible' : 'hidden')};
      opacity: ${({ $isOpen }) => ($isOpen ? '1' : '0')};
      transform: translateY(${({ $isOpen }) => ($isOpen ? '0' : '-30%')});
      transition: transform 0.3s ease-in-out, opacity 0.3s ease-in-out, visibility 0.3s ease-in-out;
      width: 60%;
      min-width: 200px;
    }
  }
`

const WrapperButton = styled.div`
  margin-bottom: ${({ theme }) => theme.padding(1)};
`

const MobileButton = styled(ButtonVariants)`
  width: 40px;
  height: 40px;
  box-shadow: ${({ theme }) => theme.shadow(1)};
  transition: box-shadow 0.2s ease-in-out;
  &:hover {
    box-shadow: ${({ theme }) => theme.shadow(2)};
  }
`

const ViewSelectDesktopBoxWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: row;

  small,
  span,
  h2 {
    color: #363636;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}px) {
    flex-direction: column;
    max-width: none;
  }

  button {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
    background: transparent;
    border: none;
    box-shadow: none;
    border-radius: 0;
    padding: ${({ theme }) => theme.padding(2)};
    cursor: pointer;
    text-align: left;
    transition: background-color, box-shadow;
    transition-duration: ${({ theme }) => theme.transitionDurationMs}ms;
    transition-timing-function: ${({ theme }) => theme.transitionTimingFunction};
    border-bottom: 1px solid #cacaca;

    &:hover {
      background-color: #fff;
      box-shadow: ${({ theme }) => theme.shadow(2)};
      z-index: 1;
    }

    &:first-child {
      border-top-left-radius: ${({ theme }) => theme.borderRadius};
      border-bottom-left-radius: ${({ theme }) => theme.borderRadius};
      border-right: 0.5px solid rgba(0, 0, 0, 0.1);

      @media (max-width: ${({ theme }) => theme.breakpoints.mobile}px) {
        border-top-right-radius: ${({ theme }) => theme.borderRadius};
        border-bottom-left-radius: 0;
      }
    }

    &:last-child {
      border-top-right-radius: ${({ theme }) => theme.borderRadius};
      border-bottom-right-radius: ${({ theme }) => theme.borderRadius};
      border-left: 0.5px solid rgba(0, 0, 0, 0.1);

      @media (max-width: ${({ theme }) => theme.breakpoints.mobile}px) {
        border-top-right-radius: 0;
        border-bottom-left-radius: ${({ theme }) => theme.borderRadius};
      }
    }

    &:not(:first-child):not(:last-child) {
      border-left: 0.5px solid rgba(0, 0, 0, 0.1);
      border-right: 0.5px solid rgba(0, 0, 0, 0.1);
    }

    h2 {
      font-size: 0.7rem;
      font-weight: 700;
      margin: 0;
      margin-bottom: ${({ theme }) => theme.padding(1)};
      text-transform: uppercase;
    }
  }

  .column {
    flex: 1;
  }
`

export interface EditSpaceToolProps {
  currentTool?: VariableWithResult
  spacesLimitation?: { space: Partial<SpaceForPoint> }
}

export interface ViewSelectProps {
  variables: VariableAndOptionalResult[]
  baseLayers: Layer[]
  layers: Layer[]
  onChangeLayers: (layers: Layer[] | ((layers: Layer[]) => Layer[])) => void
  onChangeReport?: (report: ReportWithLayer | null) => void
  editProps?: EditSpaceToolProps
  isHighResolutionSpace?: boolean
}

export default function ViewSelect({
  baseLayers,
  layers,
  onChangeLayers,
  variables,
  onChangeReport,
  editProps,
  isHighResolutionSpace = false,
}: ViewSelectProps) {
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const ref = useRef(null)
  const isMobile = useMedia('(max-width: 768px)')
  const { context } = useAPI()
  const [isModalVariableOpen, setIsModalVariableOpen] = useState<boolean[]>(() =>
    Array.from({ length: layers.length }, () => false),
  )
  const [isModalTimeOpen, setIsModalTimeOpen] = useState<boolean[]>(() =>
    Array.from({ length: layers.length }, () => false),
  )
  const [isModalSpaceOpen, setIsModalSpaceOpen] = useState<boolean[]>(() =>
    Array.from({ length: layers.length }, () => false),
  )

  useEffect(() => {
    if (!context.user || !variables.length) {
      return
    }
    // Run only once
    if (baseLayers.length) {
      return
    }
    if (editProps && editProps?.currentTool) {
      onChangeLayers([recommendedLayerForVariable(editProps.currentTool, context.user)])
    }
  }, [context.user, variables, baseLayers, editProps])

  useClickAway(ref, () => {
    if (isOpen) {
      setIsOpen(prevData => !prevData)
    }
  })

  useEffect(() => {
    const size = layers.length
    setIsModalVariableOpen(prev => {
      if (prev.length === size) return prev
      const next = Array.from({ length: size }, (_, i) => prev[i] ?? false)
      return next
    })
    setIsModalTimeOpen(prev => {
      if (prev.length === size) return prev
      const next = Array.from({ length: size }, (_, i) => prev[i] ?? false)
      return next
    })
    setIsModalSpaceOpen(prev => {
      if (prev.length === size) return prev
      const next = Array.from({ length: size }, (_, i) => prev[i] ?? false)
      return next
    })
  }, [layers.length])

  return (
    <Wrapper ref={ref} $isOpen={isOpen}>
      {isMobile ? (
        <WrapperButton>
          <MobileButton
            icon={isOpen ? faXmark : faMagnifyingGlass}
            onClick={() => setIsOpen(prevData => !prevData)}
            variant='light'
            size='lg'
          />
        </WrapperButton>
      ) : null}

      {layers.map((layer, index) => {
        const baseLayer = baseLayers[index]
        let spaceChildrenLabel: string
        if (layer.space.childrenType === 'custom') {
          spaceChildrenLabel = layer.space.childrenTitle
        } else if (layer.space.childrenType === 'item') {
          spaceChildrenLabel =
            childrenItemViewLabels[
              layer.space.childrenItemView! as keyof typeof childrenItemViewLabels
            ]
        } else if (layer.space.childrenType === layer.space.delimitationType) {
          spaceChildrenLabel = spaceTypeSingularLabels[layer.space.childrenType]
        } else {
          spaceChildrenLabel = spaceTypePluralLabels[layer.space.childrenType]
        }
        const restrictColumn = (layer.variable as ToolsAndOptionalResult)?.restrictView

        const onChangeLayer = (value: Layer | ((oldLayer: Layer) => Layer)) => {
          onChangeLayers(oldLayers => {
            const newLayers = [...layers]
            newLayers[index] =
              typeof value === 'function'
                ? (value as (old: Layer) => Layer)(oldLayers[index])
                : value
            return newLayers
          })
        }

        return (
          <div className='layer' key={index}>
            <ViewSelectDesktopBoxWrapper>
              {!restrictColumn?.includes('variable') && (
                <button
                  className='column variable-characteristic'
                  onClick={() =>
                    setIsModalVariableOpen(prev => prev.map((v, i) => (i === index ? true : v)))
                  }
                >
                  <h2>O quê</h2>
                  <span>{layer.variable.label ? layer.variable.label : layer.variable.title}</span>
                  <small>{layer.variable.label ? layer.variable.title : null}</small>
                </button>
              )}
              {!restrictColumn?.includes('time') && (
                <button
                  className='column time'
                  onClick={() =>
                    setIsModalTimeOpen(prev => prev.map((v, i) => (i === index ? true : v)))
                  }
                >
                  <h2>Quando</h2>
                  <span>{layer.time.title}</span>
                </button>
              )}
              {!restrictColumn?.includes('space') && (
                <button
                  className='column space'
                  onClick={() =>
                    setIsModalSpaceOpen(prev => prev.map((v, i) => (i === index ? true : v)))
                  }
                >
                  <h2>Onde</h2>
                  <span>{layer.space.delimitationTitle}</span>
                  <small>{spaceChildrenLabel}</small>
                </button>
              )}
            </ViewSelectDesktopBoxWrapper>

            {!restrictColumn?.includes('variable') && (
              <ModalSelectVariable
                isOpen={isModalVariableOpen[index]}
                onClose={() =>
                  setIsModalVariableOpen(prev => prev.map((v, i) => (i === index ? false : v)))
                }
                layer={layer}
                baseLayer={baseLayer}
                onChangeLayer={onChangeLayer}
              />
            )}
            {!restrictColumn?.includes('space') && (
              <ModalSelectSpace
                layer={layer}
                isOpen={isModalSpaceOpen[index]}
                onClose={() =>
                  setIsModalSpaceOpen(prev => prev.map((v, i) => (i === index ? false : v)))
                }
                onChangeLayer={onChangeLayer}
                editProps={{ ...editProps }}
                isHighResolutionSpace={isHighResolutionSpace}
              />
            )}
            {!restrictColumn?.includes('time') && (
              <ModalSelectTime
                layer={layer}
                isOpen={isModalTimeOpen[index]}
                onClose={() =>
                  setIsModalTimeOpen(prev => prev.map((v, i) => (i === index ? false : v)))
                }
                onChangeLayer={onChangeLayer}
                onChangeReport={onChangeReport ?? (() => {})}
              />
            )}
          </div>
        )
      })}
    </Wrapper>
  )
}
